import type {
  AnalyticsProperties,
  ShellAnalytics,
} from '@wandb/forge-shell-contract';

import {pageViaGlobal, trackViaGlobal} from './segmentGlobal';

/**
 * Resolves where this app's analytics events go, in either deployment:
 *
 *   under the shell  ->  the ShellAnalytics the shell passed to `mount`,
 *                        which stamps `app_name`
 *   standalone       ->  `./segmentGlobal`, the guarded calls into
 *                        `window.analytics`, unstamped
 *
 * This is plumbing, **not** a front door for call sites. Per `./README.md`, a
 * new tracked event belongs in an `-Events.ts` file behind `makeTrackEvent`,
 * reached as `import * as Track from '../services/analytics'`. Nothing about
 * running under the shell changes that. Registration is module-scoped rather
 * than a React context only so it can reach the layers underneath, using the
 * same pattern `registerShellBasePath` and `registerShellErrorHandler`
 * (`src/shellRemote.ts`) already use.
 *
 * Layering, top to bottom:
 *
 *   Track.featureX()   the README's convention for a new event
 *     makeTrackEvent   DS annotation, sampling, logging
 *       Analytics      the input contract (non-array object)
 *         getAnalytics  <- here: pick a sink
 *           segmentGlobal / the shell's handle
 *
 * `./segmentGlobal` exists as its own module because of that order: `Analytics`
 * resolves its sink here, so the standalone handle cannot call back into
 * `Analytics` without looping.
 *
 * The upshot is that every `makeTrackEvent` and `Analytics` event carries
 * `app_name` under the shell with no call site changing, and keeps working if
 * the shell's `window.analytics` compatibility proxy is ever removed. The
 * remaining raw `window.analytics` call sites still depend on that proxy.
 */

let shellAnalytics: ShellAnalytics | undefined;

export const registerShellAnalytics = (
  analytics: ShellAnalytics | undefined
): (() => void) => {
  const previous = shellAnalytics;
  shellAnalytics = analytics;
  return () => {
    shellAnalytics = previous;
  };
};

/**
 * The standalone handle: the guarded calls in `./segmentGlobal`, which is where
 * the `initialized` check and lost-event reporting live.
 *
 * Deliberately does **not** attach `app_name`. Standalone there is only one app,
 * so the field would disambiguate nothing, and attaching it would change what
 * every existing `makeTrackEvent` and `Analytics.track` call site emits in
 * production today. Under the shell, the shell's own handle stamps it.
 */
function createStandaloneAnalytics(scope: AnalyticsProperties): ShellAnalytics {
  return {
    track(event, extra) {
      trackViaGlobal(event, {...scope, ...extra});
    },
    page(extra, options) {
      pageViaGlobal({...scope, ...extra}, options ?? {});
    },
    scoped(extra) {
      return createStandaloneAnalytics({...scope, ...extra});
    },
  };
}

const standaloneAnalytics = createStandaloneAnalytics({});

/**
 * Never null: standalone, and under a shell that does not pass an analytics
 * handle, this falls back to `window.analytics` rather than dropping the event.
 *
 * Read it per call rather than holding the result — the shell re-registers on
 * every `update`, and the handle is cleared on unmount.
 */
export const getAnalytics = (): ShellAnalytics =>
  shellAnalytics ?? standaloneAnalytics;
